import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import WebcamCapture from "../../pages/WebcamCapture";
import introJs from 'intro.js';
/*
  Filename:    Register.js
  Description: This page is the registration page for volunteers.
*/

const Register = ({ contentOpen, setContentOpen, userPermissions }) => {
  useEffect(() => {
    // Code to run only on first page load
    window.scrollTo(0, 0); // scroll to top of page
  }, []);

  const url = process.env.REACT_APP_BACKURL + "/api/foodPantry/client";
  //const url = "https://bridginghope.life/api/foodPantry/client"; //uncomment for local testing
  const navigate = useNavigate();

  const [clientInfo, setClientInfo] = useState({
    clientID: "",
    firstName: "",
    middleName: "",
    lastName: "",
    DOB: "",
    photo: "",
  });

  const [capturedImage, setCapturedImage] = useState(null); // State to store captured image

  const [phoneInfo, setPhoneInfo] = useState({
    phoneID: "",
    countryCode: "",
    areaCode: "",
    telPrefix: "",
    lineNum: "",
    isActive: true,
    isSafe: true,
    phoneType: "",
  });

  const [addressInfo, setAddressInfo] = useState({
    addressID: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    addressType: "",
  });

  const [userInfo, setUserInfo] = useState({
    userID: "",
    email: "",
    userName: "",
    password: "",
  });

  const [confirmPassword, setConfirmPassword] = useState({
    confirmPassword: "",
  });
  const [errors, setErrors] = useState([]);

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Tour state
  const [tourStage, setTourStage] = useState("none");
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [manualTour, setManualTour] = useState(false);

  // Tour steps
  const mainTourSteps = [
    {
      element: ".register-client-title",
      intro: "This is the Register Client page. Here you can register a new client.",
      position: "bottom"
    },
    {
      element: ".register-client-body",
      intro: "Fill out all required fields to register a client. Make sure to enter accurate information.",
      position: "top"
    }
  ];

  const validateInputs = () => {
    const nameReg = /^[A-Za-z'-]+$/;
    const opNameReg = /^[A-Za-z'-]*$/;
    const dateReg = /\b(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])\b/;

    const add1Reg = /^[A-Za-z0-9\s,.-]+$/;
    const add2Reg = /^[A-Za-z0-9\s,.-]*$/;
    const cityReg = /^[A-Za-z\s'-]+$/;
    const zipReg = /^\d{5}(-\d{4})?$/;

    const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const userNameReg = /^[a-zA-Z0-9._-]{3,15}$/;
    const passReg =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;

    const stringReg = /^[A-Za-z0-9 .,!?'-]+$/;
    const numReg = /^\d+$/;
    const boolReg = /^(true|false)$/;

    let newErrors = [];

    // Client information validation
    if (!nameReg.test(clientInfo.firstName)) {
      newErrors.push("First name must only contain letters, (') or (-)");
    }
    if (!opNameReg.test(clientInfo.middleName)) {
      console.log(clientInfo.middleName);
      newErrors.push("Middle name must only contain letters, (') or (-)");
    }
    if (!nameReg.test(clientInfo.lastName)) {
      newErrors.push("Last name must only contain letters, (') or (-)");
    }
    if (!dateReg.test(clientInfo.DOB)) {
      console.log(clientInfo.DOB);
      newErrors.push("You must enter a valid date");
    }

    // Address information validation
    if (!add1Reg.test(addressInfo.addressLine1)) {
      newErrors.push(
        "Address Line 1 must only contain letters, numbers, whitespace, (,), (.) or (-)"
      );
    }
    if (!add2Reg.test(addressInfo.addressLine2)) {
      newErrors.push(
        "Address Line 2 must only contain letters, numbers, whitespace, (,), (.) or (-)"
      );
    }
    if (!cityReg.test(addressInfo.city)) {
      newErrors.push("City must only contain letters, whitespace, (') or (-)");
    }
    if (addressInfo.state === "default") {
      newErrors.push("Please select a state");
    }
    if (!zipReg.test(addressInfo.zip)) {
      newErrors.push(
        "Zip code must be a number and must be in the format 11111 or 11111-1111"
      );
    }
    if (addressInfo.addressType === "default") {
      newErrors.push("Please select a address type");
    }

    // Phone number validation
    if (!numReg.test(Number(phoneInfo.countryCode))) {
      newErrors.push("Country code must be a number");
    }
    if (!numReg.test(Number(phoneInfo.areaCode))) {
      newErrors.push("Area code must be a number");
    }
    if (!numReg.test(Number(phoneInfo.telPrefix))) {
      newErrors.push("Telephone prefix must be a number");
    }
    if (!numReg.test(Number(phoneInfo.lineNum))) {
      newErrors.push("Line number must be a number");
    }
    if (!boolReg.test(phoneInfo.isActive)) {
      newErrors.push("The phone number must be active or not");
    }
    if (!boolReg.test(phoneInfo.isSafe)) {
      newErrors.push("The phone number must be safe or not");
    }
    if (phoneInfo.phoneType === "default") {
      newErrors.push("Please select a phone type");
    }

    // User information validation
    if (!emailReg.test(userInfo.email)) {
      newErrors.push("Please enter a valid email address");
    }
    if (!userNameReg.test(userInfo.userName)) {
      newErrors.push(
        "Username must be between 3 and 15 characters and only include alphanumeric, (.), (_) or (-)"
      );
    }
    if (!passReg.test(userInfo.password)) {
      newErrors.push(
        `Password must be at least 8 characters and must include at least a capital and lowercase letter, a number, and a special character consisting of !@#$%^&*(),.?":{}|<></>`
      );
    } else {
      if (userInfo.password !== confirmPassword.confirmPassword) {
        newErrors.push("Password and Confirm Password must be the same");
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleChange = (event) => {
    const { dataset, name, value } = event.target;

    if (dataset.type === "client") {
      setClientInfo({
        ...clientInfo,
        [name]: value,
      });
    }
    if (dataset.type === "phone") {
      if (name === "phoneNumber") {
        let areaCode = value.slice(0, 3);
        let telPrefix = value.slice(3, 6);
        let lineNum = value.slice(6, 10);
        setPhoneInfo({
          ...phoneInfo,
          areaCode: areaCode,
          telPrefix: telPrefix,
          lineNum: lineNum,
        });
      } else {
        setPhoneInfo({
          ...phoneInfo,
          [name]: value,
        });
      }
    }
    if (dataset.type === "address") {
      setAddressInfo({
        ...addressInfo,
        [name]: value,
      });
    }
    if (dataset.type === "user") {
      setUserInfo({
        ...userInfo,
        [name]: value,
      });
    }
    if (dataset.type === "confirmPassword") {
      setConfirmPassword({
        ...confirmPassword,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateInputs();

    if (isValid) {
      let params = {
        client: clientInfo,
        phone: phoneInfo,
        address: addressInfo,
        user: userInfo,
      };

      try {
        await axios.post(url, params, { withCredentials: true });
        setShowSuccessModal(true); // Show success modal
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error registering user";
        setErrors({ message: errorMessage });
        alert("Registration Failed");
      }
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate("/dashboard"); // Redirect to dashboard
  };


  // Helper to check if a tour is running
  function isIntroJsActive() {
    const introInstance = window.introJs && window.introJs();
    const overlay = document.querySelector('.introjs-overlay');
    return (introInstance && introInstance._currentStep !== undefined) || (overlay && overlay.style.display !== 'none');
  }

  // Main tour effect
  useEffect(() => {
    if (tourStage === "main" && !isTourRunning && !isIntroJsActive()) {
      setIsTourRunning(true);
      setTimeout(() => {
        const intro = introJs();
        intro.setOptions({
          steps: mainTourSteps,
          showBullets: false
        });
        intro.start();
        intro.oncomplete(() => {
          setIsTourRunning(false);
          setTourStage("none");
          handleTourRedirect();
        });
        intro.onexit(() => {
          setIsTourRunning(false);
          setTourStage("none");
          setTourRanOnce(true); // Prevent auto-restart after exit
          if (manualTour) {
            setManualTour(false);
          }
        });
      }, 300); // 300ms delay before starting tour
    }
  }, [tourStage, isTourRunning]);

  // Redirection logic after tour
  function handleTourRedirect() {
    if (manualTour) {
      setManualTour(false);
      return;
    }
    if (userPermissions.includes("foodPantry")) {
      setContentOpen("foodPantry");
    } else if (userPermissions.includes("reports")) {
      setContentOpen("reports");
    } else {
      setContentOpen("searchOrganizations");
    }
  }

  // Automatic tour trigger
  useEffect(() => {
    if (!userPermissions) return;
    if (contentOpen !== "registerClient") return;
    const fullTourNeeded = JSON.parse(localStorage.getItem("FullTourNeeded"));
    if (fullTourNeeded && !isTourRunning && !isIntroJsActive()) {
      setTourStage("main");
    }
  }, [userPermissions, isTourRunning]);

  const [tourRanOnce, setTourRanOnce] = useState(false);

  // Automatic tour trigger (continuously re-prompt tour start logic)
  useEffect(() => {
    if (!userPermissions) return;
    if (contentOpen !== "registerClient") return; // Prevent polling if not on this page
    const fullTourNeeded = JSON.parse(localStorage.getItem("FullTourNeeded"));
    if (!fullTourNeeded) return;

    if (tourRanOnce) return;
    // Continuously poll for required elements and tour state
    let pollInterval = setInterval(() => {
      const titleExists = document.querySelector('.register-client-title');
      const bodyExists = document.querySelector('.register-client-body');
      const tourAlreadyRunning = isTourRunning || isIntroJsActive();
      if (titleExists && bodyExists && !tourAlreadyRunning) {
        setTourRanOnce(true); // Set before starting the tour
        setTourStage("main");
        clearInterval(pollInterval); // Stop polling after starting the tour
      }
    }, 500);

    // Cleanup
    return () => clearInterval(pollInterval);
  }, [userPermissions, isTourRunning, contentOpen]);

  // Manual tour trigger
  function handleManualTour() {
    if (isTourRunning || isIntroJsActive()) return;
    setTourStage("main");
    setManualTour(true);
  }

  return (
    <React.Fragment>
      <div className="card col-12 col-md-10 offset-0 offset-md-1 mt-5 card-body container my-3">
        <div className="card-header justify-content-between align-items-center pb-3">
          <h3>
            <span
              className="register-client-title"
              data-step="1"
            >
              <i className="bi bi-clipboard-check pe-3"></i>
              Register Client
              <i
                className="bi bi-question-circle d-none d-sm-inline"
                style={{
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#4d59df',
                  marginRight: '0.75rem',
                  marginLeft: '0.25rem'
                }}
                onClick={handleManualTour}
              />
            </span>
          </h3>
          <form className="pt-5 register-client-body" onSubmit={handleSubmit}>
            {/* first and last name */}
            <div className="card border border-light m-2">
              <div className="card-header border-bottom border-light">
                <h3 className="text-center mb-1">Client Information</h3>
              </div>

              <div className="card-body">
                <div className="row g-2">
                  <div className="col-12 col-lg-4">
                    <div className="form-floating">
                      <input
                        id="txtFirstName"
                        type="text"
                        className="form-control"
                        placeholder="John"
                        aria-label="Enter your first name here"
                        data-type="client"
                        name="firstName"
                        value={clientInfo.firstName}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="txtFirstName" className="form-label">
                        First Name
                      </label>
                    </div>
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="form-floating">
                      <input
                        id="txtMiddleName"
                        type="text"
                        className="form-control"
                        placeholder="Allan"
                        aria-label="Enter your middle name here (optional)"
                        data-type="client"
                        name="middleName"
                        value={clientInfo.middleName}
                        onChange={handleChange}
                      />
                      <label htmlFor="txtMiddleName" className="form-label">
                        Middle Name (Optional)
                      </label>
                    </div>
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="form-floating">
                      <input
                        id="txtLastName"
                        type="text"
                        className="form-control"
                        placeholder="Doe"
                        aria-label="Enter your last name here"
                        data-type="client"
                        name="lastName"
                        value={clientInfo.lastName}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="txtLastName" className="form-label">
                        Last Name
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="dateDOB"
                        type="date"
                        className="form-control"
                        placeholder="01-01-2000"
                        aria-label="Enter your Date of Birth here"
                        data-type="client"
                        name="DOB"
                        value={clientInfo.DOB}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="dateDOB" className="form-label">
                        Date of Birth
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="filProfilePic"
                        type="file"
                        className="form-control"
                        placeholder="photo.png"
                        aria-label="Upload a photo here (optional)"
                        data-type="client"
                        name="photo"
                        value={clientInfo.photo}
                        onChange={handleChange}
                      />
                      <label htmlFor="filProfilePic" className="form-label">
                        Profile Picture (Optional)
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* phone info */}

            <div className="card border border-light m-2">
              <div className="card-header border-bottom border-light">
                <h3 className="text-center mb-1">Contact Information</h3>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-12 col-lg-8">
                    <div className="input-group">
                      <div className="col-12 col-lg-3 input-group-text">
                        <select
                          id="numCountryCode"
                          type="number"
                          className="form-select form-select-floating-fix pb-0 pt-1 ps-3"
                          placeholder="+1"
                          aria-label="Select a country code here"
                          data-type="phone"
                          name="countryCode"
                          value={phoneInfo.countryCode}
                          onChange={handleChange}
                          required
                        >
                          <option value="default">--select--</option>
                          <option value="1">+1</option>
                        </select>
                      </div>

                      <div className="form-floating">
                        <input
                          id="telPhoneNumber"
                          type="tel"
                          className="form-control"
                          placeholder="1112223333"
                          aria-label="Enter your phone number here"
                          data-type="phone"
                          name="phoneNumber"
                          value={
                            phoneInfo.areaCode +
                            phoneInfo.telPrefix +
                            phoneInfo.lineNum
                          }
                          onChange={handleChange}
                          required
                        />
                        <label
                          htmlFor="telPhoneNumber"
                          className="form-label"
                        >
                          Phone Number
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-4">
                    <div className="form-floating">
                      <select
                        id="txtPhoneType"
                        type="text"
                        className="form-select form-select-floating-fix pb-0 pt-3 ps-3"
                        placeholder="Personal"
                        aria-label="Select the type of phone number you have entered here"
                        data-type="phone"
                        name="phoneType"
                        value={phoneInfo.phoneType}
                        onChange={handleChange}
                        required
                      >
                        <option value="default">--select--</option>
                        <option value="Personal">Personal</option>
                        <option value="Work">Work</option>
                        <option value="Business">Business</option>
                      </select>
                      <label htmlFor="txtPhoneType" className="form-label">
                        Type of Phone Number
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* address Info */}
            <div className="card border border-light m-2">
              <div className="card-header border-bottom border-light">
                <h3 className="text-center mb-1">Address Information</h3>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="txtAddressLine1"
                        type="text"
                        className="form-control"
                        placeholder="1234 Exampe St."
                        aria-label="Enter your street address line 1 here"
                        data-type="address"
                        name="addressLine1"
                        value={addressInfo.addressLine1}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="txtAddressLine1" className="form-label">
                        Address Line 1
                      </label>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="txtAddressLine2"
                        type="text"
                        className="form-control"
                        placeholder="APT A100"
                        aria-label="Enter your street address line 2 here (optional)"
                        data-type="address"
                        name="addressLine2"
                        value={addressInfo.addressLine2}
                        onChange={handleChange}
                      />
                      <label htmlFor="txtAddressLine2" className="form-label">
                        Address Line 2 (Optional)
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-lg-4">
                    <div className="form-floating">
                      <input
                        id="txtCity"
                        type="text"
                        className="form-control"
                        placeholder="Example City"
                        aria-label="Enter your city here"
                        data-type="address"
                        name="city"
                        value={addressInfo.city}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="txtCity" className="form-label">
                        City
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-lg-4">
                    <div className="form-floating">
                      <select
                        id="txtState"
                        type="text"
                        className="form-select form-select-floating-fix pb-0 pt-3 ps-3"
                        placeholder="Exampe State"
                        aria-label="Select your state here"
                        data-type="address"
                        name="state"
                        value={addressInfo.state}
                        onChange={handleChange}
                        required
                      >
                        <option value="default">--select--</option>
                        <option value="AL">Alabama</option>
                        <option value="AK">Alaska</option>
                        <option value="AZ">Arizona</option>
                        <option value="AR">Arkansas</option>
                        <option value="CA">California</option>
                        <option value="CO">Colorado</option>
                        <option value="CT">Connecticut</option>
                        <option value="DE">Delaware</option>
                        <option value="DC">District Of Columbia</option>
                        <option value="FL">Florida</option>
                        <option value="GA">Georgia</option>
                        <option value="HI">Hawaii</option>
                        <option value="ID">Idaho</option>
                        <option value="IL">Illinois</option>
                        <option value="IN">Indiana</option>
                        <option value="IA">Iowa</option>
                        <option value="KS">Kansas</option>
                        <option value="KY">Kentucky</option>
                        <option value="LA">Louisiana</option>
                        <option value="ME">Maine</option>
                        <option value="MD">Maryland</option>
                        <option value="MA">Massachusetts</option>
                        <option value="MI">Michigan</option>
                        <option value="MN">Minnesota</option>
                        <option value="MS">Mississippi</option>
                        <option value="MO">Missouri</option>
                        <option value="MT">Montana</option>
                        <option value="NE">Nebraska</option>
                        <option value="NV">Nevada</option>
                        <option value="NH">New Hampshire</option>
                        <option value="NJ">New Jersey</option>
                        <option value="NM">New Mexico</option>
                        <option value="NY">New York</option>
                        <option value="NC">North Carolina</option>
                        <option value="ND">North Dakota</option>
                        <option value="OH">Ohio</option>
                        <option value="OK">Oklahoma</option>
                        <option value="OR">Oregon</option>
                        <option value="PA">Pennsylvania</option>
                        <option value="RI">Rhode Island</option>
                        <option value="SC">South Carolina</option>
                        <option value="SD">South Dakota</option>
                        <option value="TN">Tennessee</option>
                        <option value="TX">Texas</option>
                        <option value="UT">Utah</option>
                        <option value="VT">Vermont</option>
                        <option value="VA">Virginia</option>
                        <option value="WA">Washington</option>
                        <option value="WV">West Virginia</option>
                        <option value="WI">Wisconsin</option>
                        <option value="WY">Wyoming</option>
                      </select>
                      <label htmlFor="txtState" className="form-label">
                        State
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-lg-4">
                    <div className="form-floating">
                      <input
                        id="numZip"
                        type="number"
                        className="form-control"
                        placeholder="11111"
                        aria-label="Enter your zip code here"
                        data-type="address"
                        name="zip"
                        value={addressInfo.zip}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="numZip" className="form-label">
                        Zip Code
                      </label>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-floating">
                      <select
                        id="txtAddressType"
                        type="text"
                        className="form-select form-select-floating-fix pb-0 pt-3 ps-3"
                        placeholder="Home"
                        aria-label="Select the type of your address that you have entered here"
                        data-type="address"
                        name="addressType"
                        value={addressInfo.addressType}
                        onChange={handleChange}
                        required
                      >
                        <option value="default">--select--</option>
                        <option value="Home">Home</option>
                        <option value="Shelter">Shelter</option>
                        <option value="Business">Business</option>
                      </select>
                      <label htmlFor="txtAddressType" className="form-label">
                        Address Type
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* username, email, password, and confirm password */}
            <div className="card border border-light m-2">
              <div className="card-header border-bottom border-light">
                <h3 className="text-center mb-1">User Information</h3>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="txtEmail"
                        type="email"
                        className="form-control"
                        placeholder="jdoe@email.com"
                        aria-label="Enter your email here"
                        autoComplete="on"
                        data-type="user"
                        name="email"
                        value={userInfo.email}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="txtEmail" className="form-label">
                        Email
                      </label>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="txtUsername"
                        type="text"
                        className="form-control"
                        placeholder="johndoe"
                        aria-label="Enter your username here"
                        autoComplete="on"
                        data-type="user"
                        name="userName"
                        value={userInfo.userName}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="txtUsername" className="form-label">
                        Username
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="txtPassword"
                        type="password"
                        className="form-control"
                        placeholder="123456789"
                        aria-label="Enter your password here"
                        autoComplete="on"
                        data-type="user"
                        name="password"
                        value={userInfo.password}
                        onChange={handleChange}
                        required
                      />
                      <label htmlFor="txtPassword" className="form-label">
                        Password
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="form-floating">
                      <input
                        id="txtConfirmPassword"
                        type="password"
                        className="form-control"
                        placeholder="123456789"
                        aria-label="Confirm your password here"
                        autoComplete="on"
                        data-type="confirmPassword"
                        name="confirmPassword"
                        value={confirmPassword.confirmPassword}
                        onChange={handleChange}
                        required
                      />
                      <label
                        htmlFor="txtConfirmPassword"
                        className="form-label"
                      >
                        Confirm Password
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {errors.length > 0 && (
              <div>
                <h3>Errors:</h3>
                <ul>
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            <button
              id="btnRegister"
              type="submit"
              className="btn btn-primary col-12"
            >
              Register
            </button>
          </form>
        </div>
      </div>

      <footer className="footer">
        <div className="footer-body d-flex justify-content-between mx-4 pb-3">
          <ul className="list-inline mb-0 p-0">
            <li className="list-inline-item">
              <a href="./dashboard/extra/privacy-policy.html">Privacy Policy</a>
            </li>
            <li className="list-inline-item">
              <a href="./dashboard/extra/terms-of-service.html">Terms of Use</a>
            </li>
          </ul>
          <div className="right-panel">
            ©<script>document.write(new Date().getFullYear())</script> Bridging
            Hope. All Rights Reserved.
          </div>
        </div>
      </footer>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Registration Successful</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Your account has been created successfully. You can now sign
                  in.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCloseModal}
                >
                  Go to Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default Register;
